import React, { useEffect, useMemo, useRef, useState } from 'react';
import sensorService from '../../services/sensorService';
import { toUtcDate } from '../../modules/sensor-data/utils/bridgeChartTime';
import {
  THRESHOLD_ACK_EVENT,
  buildThresholdStatusIconHtml,
  buildLoggerThresholdStatusIconHtml,
} from '../../modules/sensor-data/utils/sensorThresholdStatus';
import '../../modules/sensor-data/utils/sensorThresholdStatus.css';
import './BridgeMapPanel.css';

const SENSOR_STATUS_CACHE_TTL_MS = 60 * 1000;
/** Accelerometer and tiltmeter data counts only inside this window. */
const LIVE_DATA_WINDOW_MS = 6 * 60 * 60 * 1000;
/**
 * The stored sample can trail the vendor sample by about 3 hours.
 * That gap is still inside the 6-hour rule, so it does not blink.
 */
const VENDOR_TO_APP_GAP_MS = 3 * 60 * 60 * 1000;
/** Site ID voltage counts only inside this window. */
const LOGGER_VOLTAGE_WINDOW_MS = 48 * 60 * 60 * 1000;
/** Other devices blink when Sensimax battery is not above this percent. */
const OTHER_DEVICE_BATTERY_MIN_PERCENT = 25;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let mapsLoaderPromise = null;

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!mapsLoaderPromise) {
    mapsLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google.maps);
      script.onerror = () => reject(new Error('Failed to load Google Maps.'));
      document.head.appendChild(script);
    });
  }
  return mapsLoaderPromise;
};

export const getBridgeCoordinates = (bridge) => {
  const lat = parseFloat(bridge?.coordinates?.lat);
  const lng = parseFloat(bridge?.coordinates?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const normalizeId = (id) => (id == null ? '' : String(id));

const isCameraSensor = (sensor) => String(sensor?.sensorType || '') === 'Camera';

const getStatusSensors = (bridge) =>
  (bridge?.sensors || []).filter((sensor) => sensor?._id && !isCameraSensor(sensor));

const getBridgeStatusLabel = (bridge) => {
  const count = bridge?.sensors?.length ?? 0;
  if (count <= 0) return 'No sensors added';
  return `${count} sensor${count === 1 ? '' : 's'} added`;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const extractLatestReadings = (latestRes) => {
  if (!latestRes) return [];
  if (Array.isArray(latestRes)) return latestRes;
  if (Array.isArray(latestRes.data)) return latestRes.data;
  if (Array.isArray(latestRes.readings)) return latestRes.readings;
  if (latestRes.data && typeof latestRes.data === 'object') return [latestRes.data];
  if (latestRes.reading && typeof latestRes.reading === 'object') return [latestRes.reading];
  return [];
};

const resolveWifiLevel = ({ live, inactive }) => {
  if (inactive) return 'error';
  if (live) return 'live';
  return 'checking';
};

const pickBatteryVoltage = (payload) => {
  if (payload == null) return null;
  const raw = payload.batteryVoltage
    ?? payload.battery?.batteryVoltage
    ?? payload.battery?.voltage
    ?? payload.data?.batteryVoltage;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/** Sensimax other-device battery is a percent. Fall back to the voltage field when that is the percent. */
const pickSensimaxBatteryPercent = (payload) => {
  if (payload == null) return null;
  const raw = payload.batteryPercent
    ?? payload.batteryPercentage
    ?? payload.battery?.percent
    ?? payload.battery?.batteryPercent
    ?? payload.data?.batteryPercent;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return pickBatteryVoltage(payload);
};

/**
 * Battery bands as a share of the limit. Change these percentages to move every band.
 * Critical is below the significant percent. A reading on a line stays in the higher band.
 * 4 V example: normal 4.00–3.00, elevated down to 2.00, significant down to 1.50, critical below 1.50.
 */
const BATTERY_NORMAL_PERCENT = 75;
const BATTERY_ELEVATED_PERCENT = 50;
const BATTERY_SIGNIFICANT_PERCENT = 37.5;
const BATTERY_NORMAL_RATIO = BATTERY_NORMAL_PERCENT / 100;
const BATTERY_ELEVATED_RATIO = BATTERY_ELEVATED_PERCENT / 100;
const BATTERY_SIGNIFICANT_RATIO = BATTERY_SIGNIFICANT_PERCENT / 100;

const BATTERY_BAND_RANK = { unknown: 0, normal: 0, elevated: 1, significant: 2, critical: 3 };

const readCustomBatteryVolts = (device) => {
  const normal = Number(device?.batteryNormalVolts);
  const elevated = Number(device?.batteryElevatedVolts);
  const significant = Number(device?.batterySignificantVolts);
  if (![normal, elevated, significant].every((value) => Number.isFinite(value) && value > 0)) return null;
  if (!(significant < elevated && elevated < normal)) return null;
  return { normal, elevated, significant };
};

const classifyBatteryBand = (voltage, device) => {
  if (voltage == null || !Number.isFinite(Number(voltage)) || Number(voltage) <= 0) return 'unknown';
  const volts = Number(voltage);
  const custom = readCustomBatteryVolts(device);
  if (custom) {
    if (volts < custom.significant) return 'critical';
    if (volts < custom.elevated) return 'significant';
    if (volts < custom.normal) return 'elevated';
    return 'normal';
  }
  const limit = Number(device?.batteryVoltageLimit);
  if (!Number.isFinite(limit) || limit <= 0) return 'unknown';
  if (volts < limit * BATTERY_SIGNIFICANT_RATIO) return 'critical';
  if (volts < limit * BATTERY_ELEVATED_RATIO) return 'significant';
  if (volts < limit * BATTERY_NORMAL_RATIO) return 'elevated';
  return 'normal';
};

const resolveBatteryBand = (voltage, device) => {
  const fromVoltage = classifyBatteryBand(voltage, device);
  if (fromVoltage !== 'unknown') return fromVoltage;
  const saved = device?.lastBatteryAlertSeverity;
  if (saved === 'elevated' || saved === 'significant' || saved === 'critical' || saved === 'normal') {
    return saved;
  }
  return 'unknown';
};

const worstBatteryBand = (bands) =>
  (bands || []).reduce((worst, band) => (
    (BATTERY_BAND_RANK[band] || 0) > (BATTERY_BAND_RANK[worst] || 0) ? band : worst
  ), 'normal');

/** Elevated stays on the internal map. Significant is steady. Critical blinks the locator pin. */
const visibleBatteryBand = (band, role) => {
  if (band === 'critical' || band === 'significant') return band;
  if (band === 'elevated' && role === 'super_admin') return 'elevated';
  return 'normal';
};

const LOCATOR_PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';

const batteryMarkerIcon = (maps, band) => {
  if (band !== 'elevated' && band !== 'significant') return null;
  return {
    path: LOCATOR_PIN_PATH,
    fillColor: band === 'elevated' ? '#e0c020' : '#e07020',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 1,
    scale: 1.5,
    anchor: new maps.Point(12, 22),
  };
};

const buildLatestReadingLookup = (latestRes) => {
  const byId = new Map();
  const bySerial = new Map();
  extractLatestReadings(latestRes).forEach((reading) => {
    const id = normalizeId(reading.sensorId || reading._id);
    const serial = String(reading.sensorSerialNo || '').trim();
    if (id) byId.set(id, reading);
    if (serial) bySerial.set(serial, reading);
  });
  return { byId, bySerial };
};

const resolveLatestReading = (sensor, latestLookup) =>
  latestLookup?.byId.get(normalizeId(sensor._id))
  ?? latestLookup?.bySerial.get(String(sensor.sensorSerialNo || '').trim())
  ?? null;

const resolveLoggerReading = (logger, latestLookup) =>
  latestLookup?.byId.get(normalizeId(logger._id))
  ?? latestLookup?.bySerial.get(String(logger.serialNo || '').trim())
  ?? null;

const activeDataLoggers = (bridge) =>
  (bridge?.dataLoggers || []).filter((logger) => logger && logger.isActive !== false);

const sensorKind = (sensor) => {
  const type = String(sensor?.sensorType || '').toLowerCase();
  if (type === 'accelerometer') return 'accelerometer';
  if (type === 'tiltmeter') return 'tiltmeter';
  return 'other';
};

/** Site ID is working only when voltage above 0 arrived within the last 48 hours. */
const loggerVoltageOk = (reading) => {
  const ms = pickReadingTimeMs(reading);
  if (ms == null || Date.now() - ms > LOGGER_VOLTAGE_WINDOW_MS) return false;
  const voltage = pickBatteryVoltage(reading);
  return voltage != null && voltage > 0;
};

const pickReadingTimeMs = (reading) => {
  const raw = reading?.timestamp ?? reading?.Time ?? reading?.latest_time ?? reading?.latestTime;
  if (!raw) return null;
  const ms = toUtcDate(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** Chart data: a real X, Y, Z, or single value, not an empty status row. */
const hasPlottedData = (reading) =>
  ['xAxis', 'yAxis', 'zAxis', 'value'].some((key) => Number.isFinite(Number(reading?.[key])));

/**
 * Blink for lag only when Resensys has a newer data point than the one we stored.
 * A quiet device whose latest point is already on the chart is not behind.
 */
const isBehindResensysData = (reading) => {
  const ourMs = pickReadingTimeMs(reading);
  const vendorRaw = reading?.resensysLatestTime
    ?? reading?.vendorLatestTime
    ?? reading?.resensysTimestamp;
  if (ourMs == null || vendorRaw == null || vendorRaw === '') return false;
  const vendorMs = toUtcDate(vendorRaw).getTime();
  if (!Number.isFinite(vendorMs)) return false;
  return vendorMs - ourMs > LIVE_DATA_WINDOW_MS;
};

const statusFromLatestReading = (reading) => {
  const ms = pickReadingTimeMs(reading);
  if (ms == null) return { live: false, inactive: true };
  const live = Date.now() - ms <= LIVE_DATA_WINDOW_MS;
  return { live, inactive: !live };
};

const mapLatestReadingsToSensorStatuses = (sensors, latestLookup) =>
  sensors.map((sensor) => {
    const reading = resolveLatestReading(sensor, latestLookup);
    const { live, inactive } = statusFromLatestReading(reading);
    const batteryVoltage = pickBatteryVoltage(reading);
    const batterySeverity = resolveBatteryBand(batteryVoltage, sensor);
    const measuredBatteryBand = classifyBatteryBand(batteryVoltage, sensor);
    return {
      id: normalizeId(sensor._id),
      serial: sensor.sensorSerialNo || '',
      sensorType: sensor.sensorType || '',
      dataLoggerId: normalizeId(sensor.dataLoggerId),
      live,
      inactive,
      batteryVoltage,
      batterySeverity,
      measuredBatteryBand,
      batteryAlert: (BATTERY_BAND_RANK[batterySeverity] || 0) > 0,
      wifiLevel: resolveWifiLevel({ live, inactive }),
      specLimits: sensor.specLimits,
      reading,
    };
  });

const loggerBatteryBands = (bridge, latestLookup) =>
  activeDataLoggers(bridge).map((logger) => (
    resolveBatteryBand(pickBatteryVoltage(resolveLoggerReading(logger, latestLookup)), logger)
  ));

/** Plotted sensor data means this Site ID is working. Age alone does not. */
const sensorDataComing = (sensor) => {
  if (hasPlottedData(sensor?.reading) && !isBehindResensysData(sensor.reading)) return true;
  if (sensor?.live) return true;
  const kind = sensorKind(sensor);
  if (kind !== 'accelerometer' && kind !== 'tiltmeter') return false;
  const ms = pickReadingTimeMs(sensor?.reading);
  if (ms == null) return false;
  return Date.now() - ms <= LIVE_DATA_WINDOW_MS + VENDOR_TO_APP_GAP_MS;
};

const mapLoggerBlinkStatuses = (bridge, latestLookup, sensorStatuses = []) =>
  activeDataLoggers(bridge).map((logger) => {
    const loggerId = normalizeId(logger._id);
    const reading = resolveLoggerReading(logger, latestLookup);
    const ownVoltageOk = loggerVoltageOk(reading);
    const dataComing = sensorStatuses.some((sensor) => (
      sensor.dataLoggerId === loggerId && sensorDataComing(sensor)
    ));
    const sensorVoltageOk = sensorStatuses.some((sensor) => (
      sensor.dataLoggerId === loggerId
      && pickBatteryVoltage(sensor.reading) != null
      && pickBatteryVoltage(sensor.reading) > 0
    ));
    const voltageOk = ownVoltageOk || sensorVoltageOk || dataComing;
    return {
      id: loggerId,
      voltageOk,
      signalBlink: !voltageOk,
      measuredBatteryBand: classifyBatteryBand(pickBatteryVoltage(reading), logger),
    };
  });

/** Case 2 runs only for sensors on a Site ID whose voltage is above 0. */
const applySensorBlink = (sensorStatuses, loggerStatuses) => {
  const voltageOkById = new Map((loggerStatuses || []).map((logger) => [logger.id, logger.voltageOk]));
  return sensorStatuses.map((sensor) => {
    if (voltageOkById.get(sensor.dataLoggerId) !== true) {
      return { ...sensor, signalBlink: false };
    }
    const kind = sensorKind(sensor);
    if (kind === 'accelerometer' || kind === 'tiltmeter') {
      if (isBehindResensysData(sensor.reading)) {
        return { ...sensor, signalBlink: true };
      }
      const ms = pickReadingTimeMs(sensor.reading);
      const ageMs = ms == null ? Number.POSITIVE_INFINITY : Date.now() - ms;
      return { ...sensor, signalBlink: ageMs > LIVE_DATA_WINDOW_MS };
    }
    const percent = pickSensimaxBatteryPercent(sensor.reading);
    return {
      ...sensor,
      signalBlink: !(percent != null && percent > OTHER_DEVICE_BATTERY_MIN_PERCENT),
    };
  });
};

const buildSensorStatusCacheEntry = (sensorStatuses, loggerBands = [], loggers = []) => {
  const allKnown =
    sensorStatuses.length > 0 &&
    sensorStatuses.every((sensor) => sensor.wifiLevel !== 'checking');
  return {
    loading: false,
    sensors: sensorStatuses,
    loggers,
    hasInactive: sensorStatuses.some((sensor) => sensor.inactive || sensor.wifiLevel === 'error'),
    hasLowBattery: sensorStatuses.some((sensor) => sensor.wifiLevel === 'low'),
    hasBatteryAlert: sensorStatuses.some((sensor) => sensor.batteryAlert) || loggerBands.some((band) => (BATTERY_BAND_RANK[band] || 0) > 0),
    batteryBand: worstBatteryBand([
      ...sensorStatuses.map((sensor) => sensor.batterySeverity),
      ...loggerBands,
    ]),
    liveBatteryCritical: sensorStatuses.some((sensor) => sensor.measuredBatteryBand === 'critical')
      || (loggers || []).some((logger) => logger.measuredBatteryBand === 'critical'),
    at: allKnown ? Date.now() : 0,
  };
};

const emptyLoadingStatus = () => ({
  loading: true,
  sensors: [],
  loggers: [],
  hasInactive: false,
  hasLowBattery: false,
  hasBatteryAlert: false,
  batteryBand: 'normal',
  at: 0,
});

/** Hover pulse follows the Signal icons. A blink is violet. All working icons are blue. */
const resolveHoverPulseVariant = (status) => {
  if (!status || status.loading) return null;
  const sensors = status.sensors || [];
  const issueBlink = (status.loggers || []).some((logger) => logger.signalBlink)
    || sensors.some((sensor) => sensor.signalBlink);
  if (issueBlink) return 'error';
  if (status.hasLowBattery) return 'low';
  if (sensors.length > 0 && sensors.every((sensor) => signalDisplayLevel(sensor) === 'live')) return 'live';
  if (status.hasInactive) return 'error';
  if (sensors.length > 0 && sensors.every((sensor) => sensor.wifiLevel === 'live')) return 'live';
  return null;
};

const PULSE_VARIANTS = new Set(['error', 'low', 'live']);

const pulseOverlayClassName = (variant) => {
  const resolved = PULSE_VARIANTS.has(variant) ? variant : 'live';
  return `bridge-map-pulse-overlay bridge-map-pulse-overlay--${resolved}`;
};

const PULSE_OVERLAY_HTML =
  '<svg class="bridge-map-pulse-overlay__rings" viewBox="0 0 44 44" aria-hidden="true">'
  + '<g fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" transform="translate(22 22)">'
  + '<g class="bridge-map-pulse-arc bridge-map-pulse-arc--outer">'
  + '<circle r="18" pathLength="360" stroke-dasharray="108 72" stroke-dashoffset="54" />'
  + '</g>'
  + '<g class="bridge-map-pulse-arc bridge-map-pulse-arc--mid">'
  + '<circle r="13" pathLength="360" stroke-dasharray="108 72" stroke-dashoffset="12" />'
  + '</g>'
  + '<g class="bridge-map-pulse-arc bridge-map-pulse-arc--inner">'
  + '<circle r="8" pathLength="360" stroke-dasharray="108 72" stroke-dashoffset="90" />'
  + '</g>'
  + '</g></svg>';

const getDataLoggerLabel = (bridge, dataLoggerId) => {
  const logger = (bridge?.dataLoggers || []).find((item) => normalizeId(item._id) === normalizeId(dataLoggerId));
  return logger?.serialNo || '';
};

const groupSensorsByDataLogger = (bridge, sensorStatuses) => {
  const groups = [];
  const indexById = new Map();

  (bridge?.dataLoggers || [])
    .filter((logger) => logger && logger.isActive !== false)
    .forEach((logger) => {
      const id = normalizeId(logger._id);
      if (!id || indexById.has(id)) return;
      indexById.set(id, groups.length);
      groups.push({ id, label: logger.serialNo || id, sensors: [] });
    });

  sensorStatuses.forEach((sensor) => {
    const loggerId = sensor.dataLoggerId || '';
    if (loggerId && indexById.has(loggerId)) {
      groups[indexById.get(loggerId)].sensors.push(sensor);
      return;
    }
    const fallbackId = loggerId || '__unassigned__';
    if (!indexById.has(fallbackId)) {
      indexById.set(fallbackId, groups.length);
      groups.push({
        id: fallbackId,
        label: getDataLoggerLabel(bridge, loggerId) || (loggerId ? loggerId : 'Unassigned'),
        sensors: [],
      });
    }
    groups[indexById.get(fallbackId)].sensors.push(sensor);
  });

  return groups.filter((group) => group.sensors.length > 0);
};

const wifiLevelTitle = (sensor) => {
  if (sensor.wifiLevel === 'live') return `${sensor.serial || 'Sensor'} — Working`;
  if (sensor.wifiLevel === 'low') return `${sensor.serial || 'Sensor'} — Low battery`;
  if (sensor.wifiLevel === 'error') return `${sensor.serial || 'Sensor'} — Problem`;
  return `${sensor.serial || 'Sensor'} — Checking`;
};

const resolveLoggerWifiLevel = (sensors) => {
  if (!sensors?.length) return 'checking';
  if (sensors.some((sensor) => sensor.wifiLevel === 'error' || sensor.inactive)) return 'error';
  if (sensors.some((sensor) => sensor.wifiLevel === 'low')) return 'low';
  if (sensors.every((sensor) => sensor.wifiLevel === 'live')) return 'live';
  return 'checking';
};

const loggerWifiTitle = (group, level) => {
  const name = group.label || 'Data Logger';
  if (level === 'live') return `${name} — All sensors working`;
  if (level === 'low') return `${name} — Low battery`;
  if (level === 'error') return `${name} — Sensor problem`;
  return `${name} — Checking`;
};

const sensorBlinkTitle = (sensor) => {
  const name = sensor.serial || 'Sensor';
  if (sensorKind(sensor) === 'other') return `${name} — Battery at or below 25%`;
  return `${name} — No data in the last 6 hours`;
};

const wifiIconHtml = ({ level, title, logger = false, blink = false }) => {
  const levelClass = level && level !== 'checking' ? ` bridge-map-sensor-icon--wifi-${level}` : '';
  const loggerClass = logger ? ' bridge-map-sensor-icon--wifi-logger' : '';
  const blinkClass = blink ? ' bridge-map-sensor-icon--wifi-blink' : '';
  return `<span class="bridge-map-sensor-icon bridge-map-sensor-icon--wifi${loggerClass}${levelClass}${blinkClass}" title="${escapeHtml(title)}"><i class="ti ti-wifi"></i></span>`;
};

const sensorsFromBridge = (bridge) =>
  getStatusSensors(bridge).map((sensor) => ({
    id: normalizeId(sensor._id),
    serial: sensor.sensorSerialNo || '',
    sensorType: sensor.sensorType || '',
    dataLoggerId: normalizeId(sensor.dataLoggerId),
    wifiLevel: 'checking',
    live: false,
    inactive: false,
    specLimits: sensor.specLimits,
    reading: null,
  }));

const SENSOR_TYPE_RANK = { Accelerometer: 0, Tiltmeter: 1 };

const sensorsInTooltipOrder = (sensors) =>
  sensors
    .map((sensor, index) => ({ sensor, index }))
    .sort((a, b) => {
      const rankA = SENSOR_TYPE_RANK[a.sensor.sensorType] ?? 2;
      const rankB = SENSOR_TYPE_RANK[b.sensor.sensorType] ?? 2;
      return rankA - rankB || a.index - b.index;
    })
    .map((item) => item.sensor);

const signalDisplayLevel = (sensor) => {
  const kind = sensorKind(sensor);
  if (kind !== 'accelerometer' && kind !== 'tiltmeter') return sensor.wifiLevel;
  if (hasPlottedData(sensor.reading) && !isBehindResensysData(sensor.reading)) return 'live';
  const ms = pickReadingTimeMs(sensor.reading);
  if (ms == null) return sensor.wifiLevel;
  if (Date.now() - ms <= LIVE_DATA_WINDOW_MS + VENDOR_TO_APP_GAP_MS) return 'live';
  return sensor.wifiLevel;
};

const buildWifiRowHtml = (sensor, { clickable = false, bridgeId = '' } = {}) => {
  const name = escapeHtml(sensor.serial || sensor.id || 'Sensor');
  const typeLabel = escapeHtml(sensor.sensorType || 'Sensor');
  const canLink = clickable && (sensor.id || sensor.serial);
  const label = canLink
    ? `<button type="button" class="bridge-map-sensor-link" data-bridge-id="${escapeHtml(bridgeId)}" data-sensor-id="${escapeHtml(sensor.id)}" data-sensor-serial="${escapeHtml(sensor.serial || '')}">${name}</button>`
    : `<span class="bridge-map-sensor-name">${name}</span>`;
  const thresholdIcon = buildThresholdStatusIconHtml({
    sensor,
    reading: sensor.reading,
    wifiBlinking: sensor.wifiLevel === 'error',
  });
  const signalLevel = signalDisplayLevel(sensor);
  const signalBlink = Boolean(sensor.signalBlink) || signalLevel === 'error';
  return `<div class="bridge-map-sensor-row"><span class="bridge-map-site-kind">${typeLabel}</span>${label}<span class="bridge-map-signal-cell">${wifiIconHtml({
    level: signalLevel,
    title: signalBlink ? sensorBlinkTitle(sensor) : wifiLevelTitle({ ...sensor, wifiLevel: signalLevel }),
    blink: signalBlink,
  })}</span><span class="bridge-map-alert-cell">${thresholdIcon}</span></div>`;
};

const buildStatusInnerHtml = (bridge, statusState, { clickable = false } = {}) => {
  const statusSensors = getStatusSensors(bridge);
  if (statusSensors.length === 0) {
    return escapeHtml(getBridgeStatusLabel(bridge));
  }

  const sensorRows = (statusState && !statusState.loading && Array.isArray(statusState.sensors) && statusState.sensors.length)
    ? statusState.sensors
    : sensorsFromBridge(bridge);

  const groups = groupSensorsByDataLogger(bridge, sensorRows);
  const groupedHtml = groups
    .map((group) => {
      const signalSensors = group.sensors.map((sensor) => {
        const level = signalDisplayLevel(sensor);
        if (level === sensor.wifiLevel) return sensor;
        return { ...sensor, wifiLevel: level, inactive: level !== 'live' };
      });
      const loggerLevel = resolveLoggerWifiLevel(signalSensors);
      const loggerVoltageBlink = Boolean((statusState?.loggers || []).find((logger) => logger.id === group.id)?.signalBlink);
      const sidBlink = loggerVoltageBlink || group.sensors.some((sensor) => sensor.signalBlink);
      const loggerLabel = escapeHtml(group.label || 'Data Logger');
      const rows = sensorsInTooltipOrder(group.sensors)
        .map((sensor) => buildWifiRowHtml(sensor, { clickable, bridgeId: normalizeId(bridge._id) }))
        .join('');
      const loggerThresholdIcon = buildLoggerThresholdStatusIconHtml(group.sensors);
      const sidName = group.label || 'Data Logger';
      return `<div class="bridge-map-datalogger-group"><div class="bridge-map-datalogger-label"><span class="bridge-map-site-kind">Site ID</span><span class="bridge-map-datalogger-name">${loggerLabel}</span><span class="bridge-map-signal-cell">${wifiIconHtml({
        level: loggerLevel,
        title: loggerVoltageBlink ? `${sidName} — No voltage in the last 48 hours` : loggerWifiTitle(group, loggerLevel),
        blink: sidBlink,
      })}</span><span class="bridge-map-alert-cell">${loggerThresholdIcon}</span></div><div class="bridge-map-datalogger-sensors">${rows}</div></div>`;
    })
    .join('');

  return `<div class="bridge-map-tooltip__sensors"><span class="bridge-map-col-head"></span><span class="bridge-map-col-head"></span><span class="bridge-map-col-head">Signal</span><span class="bridge-map-col-head">Alerts</span>${groupedHtml}</div>`;
};

/** In-flight latest-reading calls, started as soon as bridge ids are known. */
const prefetchedLatestByBridgeId = new Map();

export const prefetchBridgeLatestStatuses = (bridgeIds) => {
  (bridgeIds || []).forEach((rawId) => {
    const key = rawId == null ? '' : String(rawId);
    if (!key || prefetchedLatestByBridgeId.has(key)) return;
    prefetchedLatestByBridgeId.set(
      key,
      sensorService.getLatest(key).catch(() => null)
    );
  });
};

const takePrefetchedLatest = (bridgeId) => {
  const key = normalizeId(bridgeId);
  if (!key || !prefetchedLatestByBridgeId.has(key)) return null;
  const pending = prefetchedLatestByBridgeId.get(key);
  prefetchedLatestByBridgeId.delete(key);
  return pending;
};

const fetchBridgeLatestStatuses = async (bridge) => {
  const sensors = getStatusSensors(bridge);
  const prefetched = takePrefetchedLatest(bridge._id);
  const latestRes = await (prefetched || sensorService.getLatest(bridge._id).catch(() => null));
  const latestLookup = buildLatestReadingLookup(latestRes);
  const sensorStatuses = mapLatestReadingsToSensorStatuses(sensors, latestLookup);
  const loggers = mapLoggerBlinkStatuses(bridge, latestLookup, sensorStatuses);
  const statuses = applySensorBlink(sensorStatuses, loggers);
  return {
    sensors,
    latestLookup,
    statuses,
    loggerBands: loggerBatteryBands(bridge, latestLookup),
    loggers,
  };
};

const fetchBridgeSensorStatuses = async (bridge, { onPartial } = {}) => {
  const result = await fetchBridgeLatestStatuses(bridge);
  onPartial?.(result.statuses, result.loggerBands, result.loggers);
  return result;
};

const personDisplayName = (person) =>
  String(person?.name || person?.user?.name || person?.email || person?.user?.email || '').trim();

const formatStaffNames = (list) => {
  const names = [...new Set((list || []).map(personDisplayName).filter(Boolean))];
  return names.length ? names.join(', ') : '—';
};

const buildStaffNamesHtml = (bridge) => `
  <div class="bridge-map-tooltip__row"><strong>Bridge Manager:</strong> ${escapeHtml(formatStaffNames(bridge.managers))}</div>
`;

const buildTooltipHtml = (bridge, statusState, { showSensorStatus = false, clickableSensors = false } = {}) => `
  <div class="bridge-map-tooltip">
    <div class="bridge-map-tooltip__row"><strong>Bridge:</strong> ${escapeHtml(bridge.name || '—')}</div>
    <div class="bridge-map-tooltip__row"><strong>City:</strong> ${escapeHtml(bridge.city || '—')}</div>
    ${showSensorStatus
      ? `<div class="bridge-map-tooltip__row bridge-map-tooltip__row--sensors">${buildStatusInnerHtml(bridge, statusState, { clickable: clickableSensors })}</div>`
      : buildStaffNamesHtml(bridge)}
  </div>
`;

const tooltipHtmlFor = (bridge, statusState, clickableSensors) =>
  buildTooltipHtml(bridge, statusState, {
    showSensorStatus: getStatusSensors(bridge).length > 0,
    clickableSensors,
  });

const syncMarkerInteraction = (
  maps,
  marker,
  bridge,
  isAssigned,
  onAssignedBridgeClick,
  onMarkerHover
) => {
  maps.event.clearInstanceListeners(marker);
  marker.setClickable(true);
  if (isAssigned && onAssignedBridgeClick) {
    marker.addListener('click', () => onAssignedBridgeClick(bridge));
  }
  const bridgeId = normalizeId(bridge._id);
  marker.addListener('mouseover', () => onMarkerHover?.(bridgeId));
  marker.addListener('mouseout', () => onMarkerHover?.(null));
};

/** Pan/zoom to coords and re-assert the center once tiles settle, so the map doesn't stay grey after a big zoom jump. */
const focusMapOnCoords = (map, maps, coords, zoom = 14) => {
  map.setZoom(zoom);
  map.panTo(coords);
  maps.event.addListenerOnce(map, 'idle', () => {
    maps.event.trigger(map, 'resize');
    map.panTo(coords);
  });
};

const waitForContainerSize = (element, timeoutMs = 2000) =>
  new Promise((resolve) => {
    const isReady = () => {
      const { width, height } = element.getBoundingClientRect();
      return width > 0 && height > 0;
    };

    if (isReady()) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve();
    };

    const observer = new ResizeObserver(() => {
      if (isReady()) finish();
    });
    observer.observe(element);
    const timer = window.setTimeout(finish, timeoutMs);
  });

const updateMapViewport = (map, maps, { selectedBridge, bridges }) => {
  const selectedCoords = selectedBridge ? getBridgeCoordinates(selectedBridge) : null;
  const bridgesWithCoords = (bridges || []).filter((b) => getBridgeCoordinates(b));

  if (selectedCoords) {
    focusMapOnCoords(map, maps, selectedCoords);
    return;
  }
  if (bridgesWithCoords.length === 1) {
    focusMapOnCoords(map, maps, getBridgeCoordinates(bridgesWithCoords[0]));
    return;
  }
  if (bridgesWithCoords.length > 1) {
    const bounds = new maps.LatLngBounds();
    bridgesWithCoords.forEach((bridge) => bounds.extend(getBridgeCoordinates(bridge)));
    map.fitBounds(bounds);
    maps.event.addListenerOnce(map, 'idle', () => {
      maps.event.trigger(map, 'resize');
      if (map.getZoom() > 10) {
        map.setZoom(10);
      }
    });
    return;
  }

  maps.event.trigger(map, 'resize');
};

let PulseOverlayClass = null;
const PULSE_OVERLAY_VERSION = 5;

const getPulseOverlayClass = (maps) => {
  if (PulseOverlayClass && PulseOverlayClass.markupVersion === PULSE_OVERLAY_VERSION) {
    return PulseOverlayClass;
  }

  PulseOverlayClass = class BridgePulseOverlay extends maps.OverlayView {
    constructor(position, variant = 'live') {
      super();
      this.position = position;
      this.variant = variant;
      this.div = null;
    }

    setPulsePosition(position) {
      this.position = position;
      this.draw();
    }

    setPulseVariant(variant) {
      this.variant = variant;
      if (this.div) {
        this.div.className = pulseOverlayClassName(variant);
      }
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = pulseOverlayClassName(this.variant);
      div.setAttribute('data-pulse-v', String(PULSE_OVERLAY_VERSION));
      div.innerHTML = PULSE_OVERLAY_HTML;
      this.div = div;
      this.getPanes().overlayLayer.appendChild(div);
    }

    draw() {
      if (!this.div || !this.position) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }

    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  };
  PulseOverlayClass.markupVersion = PULSE_OVERLAY_VERSION;

  return PulseOverlayClass;
};

let TooltipOverlayClass = null;
const TOOLTIP_OVERLAY_VERSION = 4;

const getTooltipOverlayClass = (maps) => {
  if (TooltipOverlayClass && TooltipOverlayClass.markupVersion === TOOLTIP_OVERLAY_VERSION) {
    return TooltipOverlayClass;
  }

  TooltipOverlayClass = class BridgeTooltipOverlay extends maps.OverlayView {
    constructor(position, html, handlers = {}) {
      super();
      this.position = position;
      this.html = html;
      this.handlers = handlers;
      this.div = null;
    }

    setTooltipContent(position, html) {
      this.position = position;
      this.html = html;
      if (this.div) {
        this.div.innerHTML = html;
      }
      this.draw();
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = 'bridge-map-tooltip-overlay';
      div.innerHTML = this.html;
      this.div = div;
      div.addEventListener('mouseenter', () => this.handlers.onHoverChange?.(true));
      div.addEventListener('mouseleave', () => this.handlers.onHoverChange?.(false));
      div.addEventListener('click', (event) => {
        const link = event.target.closest('.bridge-map-sensor-link');
        if (!link) return;
        event.preventDefault();
        event.stopPropagation();
        this.handlers.onSensorClick?.({
          bridgeId: link.getAttribute('data-bridge-id') || '',
          sensorId: link.getAttribute('data-sensor-id') || '',
          sensorSerialNo: link.getAttribute('data-sensor-serial') || '',
        });
      });
      this.getPanes().floatPane.appendChild(div);
    }

    draw() {
      if (!this.div || !this.position) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;

      const mapDiv = this.getMap()?.getDiv?.();
      const containerPoint = projection.fromLatLngToContainerPixel?.(this.position);
      const tipWidth = this.div.offsetWidth;
      const tipHeight = this.div.offsetHeight;
      if (!mapDiv || !containerPoint || !tipWidth || !tipHeight) {
        this.div.classList.remove('is-flip-left');
        this.div.style.removeProperty('--tooltip-shift-y');
        this.div.style.removeProperty('--tooltip-arrow-top');
        return;
      }
      const mapRect = mapDiv.getBoundingClientRect();
      const gap = 22;
      const margin = 8;
      const visibleLeft = Math.max(mapRect.left, 0);
      const visibleRight = Math.min(mapRect.right, window.innerWidth);
      const visibleTop = Math.max(mapRect.top, 0);
      const pinX = mapRect.left + containerPoint.x;
      const pinY = mapRect.top + containerPoint.y;
      const spaceRight = visibleRight - pinX - gap;
      const spaceLeft = pinX - visibleLeft - gap;
      const flipLeft = spaceRight < tipWidth + margin && spaceLeft > spaceRight;
      this.div.classList.toggle('is-flip-left', flipLeft);

      const baseTop = pinY - tipHeight / 2 - 36;
      const shiftY = baseTop < visibleTop + margin
        ? Math.round((visibleTop + margin) - baseTop)
        : 0;
      if (shiftY > 0) {
        const maxArrow = Math.max(14, tipHeight - 14);
        const arrowTop = Math.round(Math.max(14, Math.min(maxArrow, tipHeight / 2 - shiftY)));
        this.div.style.setProperty('--tooltip-shift-y', `${shiftY}px`);
        this.div.style.setProperty('--tooltip-arrow-top', `${arrowTop}px`);
      } else {
        this.div.style.removeProperty('--tooltip-shift-y');
        this.div.style.removeProperty('--tooltip-arrow-top');
      }
    }

    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  };
  TooltipOverlayClass.markupVersion = TOOLTIP_OVERLAY_VERSION;

  return TooltipOverlayClass;
};

export default function BridgeMapPanel({
  bridges = [],
  assignedBridgeIds = [],
  selectedBridge,
  pulseBridgeId = null,
  pulseToken = 0,
  currentUser = null,
  onAssignedBridgeClick,
  onSensorSerialClick,
  onLoadError,
  hideHeader = false,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapsApiRef = useRef(null);
  const markersRef = useRef(new Map());
  const bridgesRef = useRef(bridges);
  const selectedBridgeRef = useRef(selectedBridge);
  const pulseOverlaysRef = useRef(new Map());
  const blinkTimersRef = useRef(new Map());
  const tooltipOverlayRef = useRef(null);
  const bridgesByIdRef = useRef(new Map());
  const sensorStatusCacheRef = useRef(new Map());
  const inFlightStatusRef = useRef(new Map());
  const hoveredBridgeIdRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [hoveredBridgeId, setHoveredBridgeId] = useState(null);
  const [sensorStatusTick, setSensorStatusTick] = useState(0);
  const onLoadErrorRef = useRef(onLoadError);
  const onAssignedBridgeClickRef = useRef(onAssignedBridgeClick);
  const onSensorSerialClickRef = useRef(onSensorSerialClick);
  const hideTooltipTimerRef = useRef(null);
  const tooltipHoveredRef = useRef(false);
  const hideTooltipFnsRef = useRef({ cancel: () => {}, schedule: () => {} });
  const onMarkerHoverRef = useRef(setHoveredBridgeId);
  const currentUserRef = useRef(currentUser);

  hideTooltipFnsRef.current.cancel = () => {
    if (hideTooltipTimerRef.current) {
      window.clearTimeout(hideTooltipTimerRef.current);
      hideTooltipTimerRef.current = null;
    }
  };

  hideTooltipFnsRef.current.schedule = () => {
    hideTooltipFnsRef.current.cancel();
    hideTooltipTimerRef.current = window.setTimeout(() => {
      hideTooltipTimerRef.current = null;
      if (tooltipHoveredRef.current) return;
      setHoveredBridgeId(null);
    }, 180);
  };

  onLoadErrorRef.current = onLoadError;
  onAssignedBridgeClickRef.current = onAssignedBridgeClick;
  onSensorSerialClickRef.current = onSensorSerialClick;
  onMarkerHoverRef.current = (bridgeId) => {
    if (bridgeId) {
      hideTooltipFnsRef.current.cancel();
      setHoveredBridgeId(bridgeId);
      return;
    }
    hideTooltipFnsRef.current.schedule();
  };
  currentUserRef.current = currentUser;
  hoveredBridgeIdRef.current = hoveredBridgeId;
  bridgesRef.current = bridges;
  selectedBridgeRef.current = selectedBridge;
  bridgesByIdRef.current = new Map(
    (bridges || []).map((bridge) => [normalizeId(bridge._id), bridge])
  );

  useEffect(() => {
    const refreshThresholdIcons = () => setSensorStatusTick((n) => n + 1);
    window.addEventListener(THRESHOLD_ACK_EVENT, refreshThresholdIcons);
    window.addEventListener('storage', refreshThresholdIcons);
    return () => {
      window.removeEventListener(THRESHOLD_ACK_EVENT, refreshThresholdIcons);
      window.removeEventListener('storage', refreshThresholdIcons);
    };
  }, []);

  // Effect 1: load Google Maps, initialize the map, and keep markers in sync.
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      onLoadErrorRef.current?.('Google Maps API key is not configured.');
      return undefined;
    }
    if (!mapRef.current) return undefined;

    const assignedIds = new Set((assignedBridgeIds || []).map(normalizeId));
    const container = mapRef.current;

    let cancelled = false;
    const bridgesWithCoords = (bridges || []).filter((b) => getBridgeCoordinates(b));

    waitForContainerSize(container)
      .then(() => loadGoogleMaps(GOOGLE_MAPS_API_KEY))
      .then((maps) => {
        if (cancelled || !mapRef.current) return;

        mapsApiRef.current = maps;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new maps.Map(mapRef.current, {
            center: { lat: 20, lng: 0 },
            zoom: 2,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          });
        }

        maps.event.trigger(mapInstanceRef.current, 'resize');

        const activeIds = new Set();

        bridgesWithCoords.forEach((bridge) => {
          const id = normalizeId(bridge._id);
          const position = getBridgeCoordinates(bridge);
          const isAssigned = assignedIds.has(id);
          activeIds.add(id);

          const existing = markersRef.current.get(id);
          if (existing) {
            existing.setPosition(position);
            existing.setTitle('');
            existing.setMap(mapInstanceRef.current);
            syncMarkerInteraction(
              maps,
              existing,
              bridge,
              isAssigned,
              onAssignedBridgeClickRef.current,
              onMarkerHoverRef.current
            );
          } else {
            const marker = new maps.Marker({
              position,
              map: mapInstanceRef.current,
              clickable: true,
            });
            syncMarkerInteraction(
              maps,
              marker,
              bridge,
              isAssigned,
              onAssignedBridgeClickRef.current,
              onMarkerHoverRef.current
            );
            markersRef.current.set(id, marker);
          }
        });

        markersRef.current.forEach((marker, id) => {
          if (!activeIds.has(id)) {
            const overlay = pulseOverlaysRef.current.get(id);
            if (overlay) {
              overlay.setMap(null);
              pulseOverlaysRef.current.delete(id);
            }
            const timer = blinkTimersRef.current.get(id);
            if (timer) {
              window.clearInterval(timer);
              blinkTimersRef.current.delete(id);
            }
            marker.setMap(null);
            markersRef.current.delete(id);
          }
        });

        setMapsReady(true);
      })
      .catch(() => onLoadErrorRef.current?.('Failed to load Google Maps.'));

    return () => {
      cancelled = true;
    };
  }, [bridges, assignedBridgeIds]);

  useEffect(() => {
    const stopMarkerPulse = (bridgeId) => {
      const id = normalizeId(bridgeId);
      const overlay = pulseOverlaysRef.current.get(id);
      if (!overlay) return;
      overlay.setMap(null);
      pulseOverlaysRef.current.delete(id);
    };

    const stopAllMarkerPulses = () => {
      pulseOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
      pulseOverlaysRef.current.clear();
    };

    const startMarkerPulse = (bridgeId, variant = 'live') => {
      const id = normalizeId(bridgeId);
      if (!id) return;

      const marker = markersRef.current.get(id);
      const map = mapInstanceRef.current;
      const maps = mapsApiRef.current;
      if (!marker || !map || !maps) return;

      const position = marker.getPosition();
      if (!position) return;

      const existing = pulseOverlaysRef.current.get(id);
      if (existing) {
        const hasRings = existing.div?.getAttribute?.('data-pulse-v') === String(PULSE_OVERLAY_VERSION);
        if (hasRings) {
          existing.setPulsePosition(position);
          existing.setPulseVariant(variant);
          return;
        }
        existing.setMap(null);
        pulseOverlaysRef.current.delete(id);
      }

      const PulseOverlay = getPulseOverlayClass(maps);
      const overlay = new PulseOverlay(position, variant);
      overlay.setMap(map);
      pulseOverlaysRef.current.set(id, overlay);
    };

    const stopMarkerBlink = (bridgeId) => {
      const id = normalizeId(bridgeId);
      const timer = blinkTimersRef.current.get(id);
      if (timer) {
        window.clearInterval(timer);
        blinkTimersRef.current.delete(id);
      }
      const marker = markersRef.current.get(id);
      if (marker) marker.setOpacity(1);
    };

    const startMarkerBlink = (bridgeId) => {
      const id = normalizeId(bridgeId);
      if (!id || blinkTimersRef.current.has(id)) return;
      const marker = markersRef.current.get(id);
      if (!marker) return;
      let visible = true;
      const timer = window.setInterval(() => {
        visible = !visible;
        const current = markersRef.current.get(id);
        if (current) current.setOpacity(visible ? 1 : 0.2);
      }, 700);
      blinkTimersRef.current.set(id, timer);
    };

    const stopAllMarkerBlinks = () => {
      Array.from(blinkTimersRef.current.keys()).forEach((id) => stopMarkerBlink(id));
    };

    if (!mapsReady) {
      stopAllMarkerPulses();
      stopAllMarkerBlinks();
      return undefined;
    }

    const desiredPulses = new Map();
    const hoveredId = normalizeId(hoveredBridgeId);
    if (hoveredId && markersRef.current.has(hoveredId)) {
      const hoverStatus = sensorStatusCacheRef.current.get(hoveredId);
      const hoverVariant = resolveHoverPulseVariant(hoverStatus);
      if (hoverVariant) desiredPulses.set(hoveredId, hoverVariant);
    }

    const pulsedId = normalizeId(pulseBridgeId);
    if (pulsedId && markersRef.current.has(pulsedId) && !desiredPulses.has(pulsedId)) {
      const pulseStatus = sensorStatusCacheRef.current.get(pulsedId);
      const pulseVariant = resolveHoverPulseVariant(pulseStatus);
      if (pulseVariant) desiredPulses.set(pulsedId, pulseVariant);
    }

    desiredPulses.forEach((variant, id) => startMarkerPulse(id, variant));
    Array.from(pulseOverlaysRef.current.keys()).forEach((id) => {
      if (!desiredPulses.has(id)) stopMarkerPulse(id);
    });

    const maps = mapsApiRef.current;
    const role = currentUserRef.current?.role;
    const blinkIds = new Set();
    const steadyBands = new Map();
    markersRef.current.forEach((_marker, id) => {
      const status = sensorStatusCacheRef.current.get(id);
      const band = visibleBatteryBand(status?.batteryBand, role);
      const sensors = status?.sensors || [];
      const sensorBlink = sensors.some((sensor) => sensor.signalBlink);
      const loggerBlink = (status?.loggers || []).some((logger) => logger.signalBlink);
      const ruleBlink = Boolean(
        status &&
        !status.loading &&
        (sensorBlink || loggerBlink)
      );
      if (ruleBlink || (band === 'critical' && status?.liveBatteryCritical)) blinkIds.add(id);
      else if (band === 'elevated' || band === 'significant') steadyBands.set(id, band);
    });
    markersRef.current.forEach((marker, id) => {
      if (blinkIds.has(id)) {
        marker.setIcon(null);
        startMarkerBlink(id);
        return;
      }
      stopMarkerBlink(id);
      marker.setIcon(maps ? batteryMarkerIcon(maps, steadyBands.get(id)) : null);
    });

    return undefined;
  }, [hoveredBridgeId, pulseBridgeId, pulseToken, mapsReady, sensorStatusTick, bridges, currentUser]);

  useEffect(() => () => {
    if (hideTooltipTimerRef.current) {
      window.clearTimeout(hideTooltipTimerRef.current);
      hideTooltipTimerRef.current = null;
    }
    pulseOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
    pulseOverlaysRef.current.clear();
    blinkTimersRef.current.forEach((timer, id) => {
      window.clearInterval(timer);
      const marker = markersRef.current.get(id);
      if (marker) marker.setOpacity(1);
    });
    blinkTimersRef.current.clear();
  }, []);

  useEffect(() => {
    const stopTooltip = () => {
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setMap(null);
        tooltipOverlayRef.current = null;
      }
    };

    const showTooltip = (bridgeId) => {
      const id = normalizeId(bridgeId);
      const bridge = bridgesByIdRef.current.get(id);
      const marker = markersRef.current.get(id);
      const map = mapInstanceRef.current;
      const maps = mapsApiRef.current;
      if (!bridge || !marker || !map || !maps) return;

      const position = marker.getPosition();
      if (!position) return;

      const statusState = sensorStatusCacheRef.current.get(id);
      const html = tooltipHtmlFor(bridge, statusState, Boolean(onSensorSerialClickRef.current));
      const TooltipOverlay = getTooltipOverlayClass(maps);

      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setTooltipContent(position, html);
        return;
      }

      const overlay = new TooltipOverlay(position, html, {
        onSensorClick: (payload) => onSensorSerialClickRef.current?.(payload),
        onHoverChange: (inside) => {
          tooltipHoveredRef.current = inside;
          if (inside) hideTooltipFnsRef.current.cancel();
          else hideTooltipFnsRef.current.schedule();
        },
      });
      overlay.setMap(map);
      tooltipOverlayRef.current = overlay;
    };

    if (!mapsReady || !hoveredBridgeId) {
      stopTooltip();
      return stopTooltip;
    }

    showTooltip(hoveredBridgeId);
    return stopTooltip;
  }, [hoveredBridgeId, mapsReady, bridges, sensorStatusTick, currentUser]);

  useEffect(() => {
    const applyFetchedStatus = (bridgeId, sensorStatuses, loggerBands, loggers) => {
      const nextStatus = buildSensorStatusCacheEntry(sensorStatuses, loggerBands, loggers);
      sensorStatusCacheRef.current.set(bridgeId, nextStatus);
      setSensorStatusTick((n) => n + 1);

      if (hoveredBridgeIdRef.current !== bridgeId) return nextStatus;

      const hoveredBridge = bridgesByIdRef.current.get(bridgeId);
      const marker = markersRef.current.get(bridgeId);
      if (hoveredBridge && marker && tooltipOverlayRef.current) {
        const position = marker.getPosition();
        if (position) {
          tooltipOverlayRef.current.setTooltipContent(
            position,
            tooltipHtmlFor(hoveredBridge, nextStatus, Boolean(onSensorSerialClickRef.current))
          );
        }
      }
      return nextStatus;
    };

    const mappedBridges = (bridges || []).filter(
      (bridge) => getBridgeCoordinates(bridge) && getStatusSensors(bridge).length > 0
    );

    const bridgesNeedingFetch = mappedBridges.filter((bridge) => {
      const bridgeId = normalizeId(bridge._id);
      const cached = sensorStatusCacheRef.current.get(bridgeId);
      const cacheIsFresh = Boolean(
        cached && !cached.loading && cached.at > 0 && Date.now() - cached.at < SENSOR_STATUS_CACHE_TTL_MS
      );
      if (cacheIsFresh) return false;
      if (inFlightStatusRef.current.has(bridgeId)) return false;
      return true;
    });

    if (!bridgesNeedingFetch.length) return undefined;

    bridgesNeedingFetch.forEach((bridge) => {
      const bridgeId = normalizeId(bridge._id);
      const cached = sensorStatusCacheRef.current.get(bridgeId);
      if (!cached?.sensors?.length) {
        sensorStatusCacheRef.current.set(bridgeId, emptyLoadingStatus());
      }
    });

    // Latest data_acquire readings only (no battery_status / charge_status / hardware status).
    const pipeline = Promise.all(
      bridgesNeedingFetch.map(async (bridge) => {
        const bridgeId = normalizeId(bridge._id);
        try {
          const { statuses, loggerBands, loggers } = await fetchBridgeLatestStatuses(bridge);
          applyFetchedStatus(bridgeId, statuses, loggerBands, loggers);
        } catch {
          // Leave loading/previous cache if latest fetch fails.
        }
      })
    ).finally(() => {
      bridgesNeedingFetch.forEach((bridge) => {
        inFlightStatusRef.current.delete(normalizeId(bridge._id));
      });
    });

    bridgesNeedingFetch.forEach((bridge) => {
      inFlightStatusRef.current.set(normalizeId(bridge._id), pipeline);
    });

    return undefined;
  }, [bridges, currentUser]);

  useEffect(() => {
    const bridgeId = normalizeId(hoveredBridgeId);
    if (!bridgeId) return undefined;

    const bridge = bridgesByIdRef.current.get(bridgeId);
    const sensors = getStatusSensors(bridge);
    if (!bridge || sensors.length === 0) return undefined;

    const cached = sensorStatusCacheRef.current.get(bridgeId);
    const cacheIsFresh = Boolean(
      cached && !cached.loading && cached.at > 0 && Date.now() - cached.at < SENSOR_STATUS_CACHE_TTL_MS
    );
    if (cacheIsFresh) return undefined;
    if (inFlightStatusRef.current.has(bridgeId)) return undefined;

    let cancelled = false;

    if (!cached?.sensors?.length) {
      sensorStatusCacheRef.current.set(bridgeId, emptyLoadingStatus());
    }

    const request = fetchBridgeSensorStatuses(bridge, {
      onPartial: (partialStatuses, loggerBands, loggers) => {
        if (cancelled) return;
        const hasKnown = partialStatuses.some((sensor) => sensor.live || sensor.inactive);
        if (!hasKnown) return;
        const nextStatus = buildSensorStatusCacheEntry(partialStatuses, loggerBands, loggers);
        sensorStatusCacheRef.current.set(bridgeId, nextStatus);
        setSensorStatusTick((n) => n + 1);
        if (hoveredBridgeIdRef.current !== bridgeId) return;
        const hoveredBridge = bridgesByIdRef.current.get(bridgeId);
        const marker = markersRef.current.get(bridgeId);
        if (hoveredBridge && marker && tooltipOverlayRef.current) {
          const position = marker.getPosition();
          if (position) {
            tooltipOverlayRef.current.setTooltipContent(
              position,
              tooltipHtmlFor(hoveredBridge, nextStatus, Boolean(onSensorSerialClickRef.current))
            );
          }
        }
      },
    }).then((result) => {
      if (cancelled) return;
      const nextStatus = buildSensorStatusCacheEntry(result.statuses, result.loggerBands, result.loggers);
      sensorStatusCacheRef.current.set(bridgeId, nextStatus);
      setSensorStatusTick((n) => n + 1);
      if (hoveredBridgeIdRef.current !== bridgeId) return;
      const hoveredBridge = bridgesByIdRef.current.get(bridgeId);
      const marker = markersRef.current.get(bridgeId);
      if (hoveredBridge && marker && tooltipOverlayRef.current) {
        const position = marker.getPosition();
        if (position) {
          tooltipOverlayRef.current.setTooltipContent(
            position,
            tooltipHtmlFor(hoveredBridge, nextStatus, Boolean(onSensorSerialClickRef.current))
          );
        }
      }
    }).finally(() => {
      inFlightStatusRef.current.delete(bridgeId);
    });
    inFlightStatusRef.current.set(bridgeId, request);

    return () => {
      cancelled = true;
    };
  }, [hoveredBridgeId, currentUser]);

  // Effect 2: update the viewport when the selection (or bridge set) changes.
  useEffect(() => {
    if (!mapsReady) return;
    const maps = mapsApiRef.current;
    const map = mapInstanceRef.current;
    if (!maps || !map) return;

    updateMapViewport(map, maps, {
      selectedBridge,
      bridges,
    });
  }, [mapsReady, selectedBridge, bridges]);

  // Effect 3: keep map tiles in sync when the container gets its final size after login/layout.
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return undefined;
    const map = mapInstanceRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps) return undefined;

    let resizeTimer = null;
    const observer = new ResizeObserver(() => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        maps.event.trigger(map, 'resize');
        updateMapViewport(map, maps, {
          selectedBridge: selectedBridgeRef.current,
          bridges: bridgesRef.current,
        });
      }, 100);
    });

    observer.observe(mapRef.current);
    return () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      observer.disconnect();
    };
  }, [mapsReady]);

  const headerBridge = selectedBridge;
  const headerCoords = headerBridge ? getBridgeCoordinates(headerBridge) : null;
  const bridgesOnMapCount = useMemo(
    () => bridges.filter((b) => getBridgeCoordinates(b)).length,
    [bridges]
  );

  return (
    <div className="bridge-map-panel">
      {!hideHeader && (
        <div className="bridge-map-panel-header">
          <div className="bridge-map-panel-title">
            <i className="ti ti-map-pin" />
            <div>
              <strong>{headerBridge ? headerBridge.name : 'All Bridges'}</strong>
              {headerCoords ? (
                <span>
                  {headerCoords.lat}, {headerCoords.lng}
                </span>
              ) : (
                <span>{bridgesOnMapCount} bridge{bridgesOnMapCount === 1 ? '' : 's'} on map</span>
              )}
            </div>
          </div>
        </div>
      )}
      {!GOOGLE_MAPS_API_KEY ? (
        <div className="bridge-map-panel-empty">
          Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in your <code>.env</code> file.
        </div>
      ) : (
        <div ref={mapRef} className="bridge-map-container" />
      )}
    </div>
  );
}
